import { Flex } from "antd";
import { Modal } from "@libertify/ui";
import { useMemo, useState } from "react";
import { useTranslate } from "@refinedev/core";
import type { ThemeResponse } from "../../../../themeEditor/hooks/useThemeApi";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { mapSlideToTemplateState } from "../../../helpers/mapTemplateSlidesToTemplateState";
import { mergeThemeWithTemplate } from "../../../helpers/mergeThemeWithTemplate";
import { SlideTemplatePreview } from "../../Template/SlideTemplatePreview";
import { getThumbnailSize } from "../../StoryboardSlidesThumbnails";
import { ThemeGroup } from "..";
import { ThemeDropdown } from "./ThemeDropdown";

const noop = () => undefined;

type ThemePreviewModalProps = {
  selectedTheme: ThemeResponse | null;
  themeGroups: ThemeGroup[];
  onSelectTheme: (theme: ThemeResponse) => void;
  onApply: () => void;
  onClose: () => void;
};

export const ThemePreviewModal = ({
  selectedTheme,
  themeGroups,
  onSelectTheme,
  onApply,
  onClose,
}: ThemePreviewModalProps) => {
  const t = useTranslate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const {
    templatesData: { presenter: initialPresenter },
    storyboardWithSlidesData: { slides },
    storyboardSections,
    mediaGenerationSettingsData: { mediaAspectRatio },
    brandKitData,
    logoRatio,
    presenterRatio,
  } = useTemplateData();

  const templates = useTemplateEditorStore((state) => state.templates);

  const previewTemplates = useMemo(() => {
    if (!selectedTheme || !mediaAspectRatio) return [];

    const commonAssets = {
      brandKitData: { logo: brandKitData.logo, audio: undefined },
      initialPresenter,
      presenterRatio,
      logoRatio,
      forceAdjustFont: true,
    };

    const results: {
      slideId: string;
      template: ReturnType<typeof mapSlideToTemplateState>;
      slide: (typeof slides)[number];
    }[] = [];

    for (const section of storyboardSections) {
      for (const slide of section.slides) {
        const existingTemplate = templates.get(slide.uuid);
        if (!existingTemplate) continue;

        const slideType = existingTemplate.slideLayoutType;
        if (!slideType) continue;

        const themeLayout = selectedTheme.layouts[slideType];
        if (!themeLayout) continue;

        const themeState = mapSlideToTemplateState(
          {
            id: "",
            sprite_definitions: themeLayout.layout,
            name: slide.uuid,
            avatarShot: existingTemplate.presenter.avatarShot,
            music: null,
            inputs: {},
          },
          slides,
          mediaAspectRatio,
          commonAssets
        );

        const slidePayload = slides.find((s) => s.uuid === slide.uuid);
        const slideHasImageElement =
          !!slidePayload && "image_element" in slidePayload.placeholders;

        const mergedTemplate = mergeThemeWithTemplate(
          existingTemplate,
          themeState,
          slideHasImageElement
        );

        results.push({ slideId: slide.uuid, template: mergedTemplate, slide });
      }
    }

    return results;
  }, [
    selectedTheme,
    mediaAspectRatio,
    brandKitData.logo,
    initialPresenter,
    presenterRatio,
    logoRatio,
    storyboardSections,
    templates,
    slides,
  ]);

  const thumbnailSize = useMemo(
    () => getThumbnailSize(mediaAspectRatio),
    [mediaAspectRatio]
  );

  return (
    <Modal
      open={!!selectedTheme}
      onClose={onClose}
      onConfirm={onApply}
      confirmText={t("media.themeEditor.apply")}
      cancelText={t("buttons.cancel")}
      title={t("components.templates.settings.themeTab.tabTitle")}
      size="large"
      styles={{
        content: {
          padding: 0,
        },
      }}
    >
      <Flex vertical gap={24} style={{ flex: 1, minWidth: 0 }}>
        <ThemeDropdown
          themeGroups={themeGroups}
          handleSelectTheme={onSelectTheme}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          label={selectedTheme?.name}
        />
        <Flex
          gap={12}
          style={{ overflowX: "auto", maxHeight: 500 }}
          wrap="nowrap"
        >
          {previewTemplates.map(({ slideId, template, slide }) => (
            <SlideTemplatePreview
              key={slideId}
              templateSize={thumbnailSize}
              border={{}}
              onCardClick={noop}
              slide={slide}
              template={template}
              opacity={1}
            />
          ))}
        </Flex>
      </Flex>
    </Modal>
  );
};
